import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import { fnGetDataSet, fnGetTableById, fnSave } from "@shared/api/restApi";
import {
  getAutoSlip,
  getInitPopUpData,
  toNum,
} from "@shared/utils/CommonUtils";
import { showResizablePopupModal } from "@widgets/CommonModal";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTab from "@shared/components/SnTab";
import SnForm from "@widgets/SnForm";
import PRD_MANUAL_CONS_POP_01 from "./PRD_MANUAL_CONS_POP_01";
import SnButton from "@shared/components/SnButton";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { menuInfo } from "@shared/type/CommonType";
import { dateToString } from "@shared/utils/DateUtils";
import {
  buildConshInsertRows,
  buildManualConslInsertRows,
  findInvalidConslIssueKeyRow,
  findYMaterialShortage,
  validateMaterialRowsBeforeSaveCommon,
} from "./WP_SAVE_COMMON";
import {
  TAB_INSPECTION,
  TAB_MATERIAL,
  TAB_REJECT,
  clampRejectQty,
  collectRowsFromGrid,
  extractRejectCodeListFromGridColumns,
  findRejectQtyError,
  getPartAvailableQty,
  isMatlRowPending,
  isYnY,
  makePartKey,
  normalizeBarcode,
} from "./WP_POPUP_COMMON";

interface popupProps {
  selectedData?: any;
  Menuprops?: menuInfo;
}

const WP2001 = forwardRef(
  ({ selectedData, Menuprops }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const formPanel1Ref = useRef<any>(null);
    const formPanel2Ref = useRef<any>(null);
    const popupRootRef = useRef<HTMLDivElement>(null);
    const prodBarcodeRef = useRef<HTMLInputElement | null>(null);
    const rejectBarcodeRef = useRef<HTMLInputElement | null>(null);
    const isRunViewMode = !!(selectedData?.no_run || "").toString().trim();
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
      grid3: React.createRef(),
    });

    const [panel1Controls, setPanel1Controls] = useState<any[]>([]);
    const [panel2Controls, setPanel2Controls] = useState<any[]>([]);
    const [panel1Data, setPanel1Data] = useState<any>({});
    const [panel2Data, setPanel2Data] = useState<any>({});
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
    });
    const [selectedDocu, setSelectedDocu] = useState<string>("");
    const [selectedLineDocu, setSelectedLineDocu] = useState<number>(0);
    const [tab, setTab] = useState<string>(TAB_REJECT);
    // 2번 테이블: 공정별 투입자재 기준 리스트(yn_matl_check/qt_unit)
    const [matlCheckRows, setMatlCheckRows] = useState<any[]>([]);
    const [popupParentEl, setPopupParentEl] = useState<HTMLElement | null>(
      null
    );
    const partsListRef = useRef<any[]>([]);
    const rejectCodeListRef = useRef<string[]>([]);
    const [hasMatlCheck, setHasMatlCheck] = useState<boolean>(false);
    // 한글 주석: 재작업(fg_run=A) 실적은 정상 실적에서 양품+불량 기준으로 자재를 이미 소진했으므로
    // 투입자재 기준 리스트를 비워 스캔 요구·소요량/재고 부족 검증·prd_consl 생성을 모두 건너뛴다.
    const isRework = (selectedData?.fg_run || "S").toString().toUpperCase() === "A";
    const shouldRestoreProdFocusRef = useRef<boolean>(false);
    const [prodBarcode, setProdBarcode] = useState<string>("");
    const [rejectBarcode, setRejectBarcode] = useState<string>("");
    const [focus, setFocus] = useState<string | null>(null);
    const toReadOnlyControls = (controls: any[]) => {
      if (!isRunViewMode) return controls;
      return (Array.isArray(controls) ? controls : []).map((control: any) => ({
        ...control,
        dc_editcon: "true",
      }));
    };

    // 현재 작업 대상 runl 연결키(no_run/no_line_run/no_barcode_item)를 얻는다.
    // 우선순위: grid1 포커스 행 -> 없으면 마지막 행.
    const getCurrentRunlLink = () => {
      const focused = gridRefs.current["grid1"].current?.focusedRowNode?.();
      if (focused?.data) {
        return {
          no_run: focused.data.no_run || "",
          no_line_run: Number(focused.data.no_line ?? 0) || 0,
          no_barcode_item: (focused.data.no_sn || "").toString(),
        };
      }

      let last: any = null;
      gridRefs.current["grid1"].current?.forEachNode?.((node: any) => {
        if (!node?.data) return;
        if (!last) {
          last = node.data;
          return;
        }
        const currNoLine = Number(node.data.no_line ?? 0) || 0;
        const lastNoLine = Number(last.no_line ?? 0) || 0;
        if (currNoLine >= lastNoLine) {
          last = node.data;
        }
      });

      return {
        no_run: last?.no_run || "",
        no_line_run: Number(last?.no_line ?? 0) || 0,
        no_barcode_item: (last?.no_sn || "").toString(),
      };
    };

    // partslist의 qt_use를 키별로 누적 갱신한다.
    const applyPartUseMap = (useMap: Map<string, number>) => {
      if (!useMap || useMap.size < 1) return;
      const next = (partsListRef.current || []).map((row: any) => {
        const key = makePartKey(row);
        if (!key) return row;
        const addUse = useMap.get(key) || 0;
        if (addUse === 0) return row;
        const nextQtUseRaw = (Number(row?.qt_use ?? 0) || 0) + addUse;
        const nextQtUse = nextQtUseRaw < 0 ? 0 : nextQtUseRaw;
        return {
          ...row,
          qt_use: nextQtUse,
          // qt_use 누적 결과로 즉시 yn_check를 동기화한다.
          // 삭제 등으로 qt_use가 0 이하가 되면 N으로 원복된다.
          yn_check: nextQtUse > 0 ? "Y" : "N",
        };
      });
      partsListRef.current = next;
    };

    // grid2 전체 row를 수집한다.
    // api가 일시적으로 비어있는 타이밍에는 getChange(insert/update)를 fallback으로 사용한다.
    const getAllGrid2Rows = () => {
      const grid2Ref = gridRefs.current["grid2"].current;
      return collectRowsFromGrid(grid2Ref, dataSources?.grid2, true);
    };

    const getAllGrid1Rows = () => {
      const grid1Ref = gridRefs.current["grid1"].current;
      return collectRowsFromGrid(grid1Ref, dataSources?.grid1, false);
    };

    // grid1(불량내역) 기준으로 패널 불량실적수량/실적수량을 보정한다.
    // 불량내역만으로는 실제 불량품 개수를 확정할 수 없으므로(getRejectQtyBounds 주석 참고)
    // 값을 덮어쓰지 않고 유효범위 안으로 clamp해 작업자가 직접 입력한 수량을 보존한다.
    const recalcRejectQtyToPanel = async () => {
      const panel2 = formPanel2Ref.current?.getFormdata?.() || {};
      const qtB = clampRejectQty(panel2.qt_b, getAllGrid1Rows());
      const qtW = toNum(panel2.qt_w);

      await formPanel2Ref.current?.setDataValue?.("qt_b", qtB, "noEvent");
      await formPanel2Ref.current?.setDataValue?.("qt", qtW + qtB, "noEvent");
    };

    // 특정 실적항번(no_line_run)에 바코드 미입력(SN/LOT) 자재가 남아있는지 확인한다.
    const hasPendingMaterialForRun = (noLineRun: number) => {
      if (!noLineRun) return false;
      return getAllGrid2Rows().some((row: any) => {
        if (Number(row?.no_line_run ?? 0) !== noLineRun) return false;
        return isMatlRowPending(row);
      });
    };

    // 전체 실적행 기준으로 아직 자재바코드가 미입력된 행이 있는지 확인한다.
    const hasAnyPendingMaterial = () => {
      return getAllGrid2Rows().some((row: any) => isMatlRowPending(row));
    };

    // 미입력 대상 중 fg_unit이 LOT/SN인 첫 번째 스캔 대상 품목을 반환한다.
    const getFirstPendingBarcodeTarget = () => {
      const rows = getAllGrid2Rows();
      const pending = rows.find((row: any) => {
        if (!isMatlRowPending(row)) return false;
        const fgUnit = (row?.fg_unit || "").toString().trim().toUpperCase();
        return fgUnit === "LOT" || fgUnit === "SN";
      });
      return pending || null;
    };

    // grid1의 cd_reject 콤보 바인딩 목록에서 스캔값 유효성 체크

    const restoreRejectBarcodeFocus = () => {
      // addRow/edit 이후 grid가 포커스를 가져가므로 다음 tick에 입력칸으로 복귀
      setTimeout(() => {
        gridRefs.current["grid1"].current?.api?.stopEditing?.();
        gridRefs.current["grid1"].current?.api?.clearFocusedCell?.();
        focusRejectBarcodeInput();
      }, 0);
    };

    // 불량 바코드가 grid1(cd_reject 콤보) 유효 목록에 포함되는지 검증한다.
    const validateRejectBarcode = (barcodeValue: string) => {
      const scan = normalizeBarcode(barcodeValue);
      if (!scan) return null;

      const codeList = Array.isArray(rejectCodeListRef.current)
        ? rejectCodeListRef.current
        : [];
      if (codeList.length < 1) return null;

      const matched = codeList.find((code: string) => code === scan);
      return matched || null;
    };

    // 화면 렌더 타이밍으로 ref가 없을 수 있어 제품바코드 입력칸 포커스를 재시도한다.
    const focusBarcodeInputWithRetry = (attempt = 0) => {
      const input = prodBarcodeRef.current;
      if (input) {
        focusProductBarcodeInput();
        return;
      }
      if (attempt < 3) {
        setTimeout(() => focusBarcodeInputWithRetry(attempt + 1), 60);
      }
    };

    // [투입자재 바코드 처리]
    // panel2(바코드/양불) 값 변경 이벤트.
    // barcode 엔터 입력 시 중복체크 -> 유효성검사 -> 행추가 -> 수량갱신 순으로 처리한다.
    const handleProductionBarcodeSubmit = async (value: any) => {
      const barcodeValue = value ?? toTrimmedString(prodBarcode);
      if (!barcodeValue) return;

      const hasPending = hasAnyPendingMaterial();
      if (!hasPending) {
        if (value) {
          return;
        }
        await showDialog("alter", "투입자재 스캔 대상이 없습니다.");
        setProdBarcode("");
        shouldRestoreProdFocusRef.current = true;
        return;
      }

      const ok = await onMaterialBarcodeScan(barcodeValue, true);
      if (!ok) {
        const target = getFirstPendingBarcodeTarget();
        const targetLabel =
          toTrimmedString(target?.nm_matl) ||
          toTrimmedString(target?.cd_matl) ||
          "대상 항목";
        await showDialog("alter", `${targetLabel}의 바코드를 입력하세요.`);
      }

      setProdBarcode("");
      shouldRestoreProdFocusRef.current = true;
    };

    // [불량 바코드 처리]
    // 불량 바코드 스캔 처리: 유효한 불량유형이면 행 추가/수량 증가
    const handleRejectBarcodeSubmit = async (rawValue?: string) => {
      const barcodeValue = toTrimmedString(rawValue ?? rejectBarcode);
      if (!barcodeValue) return;

      const validRejectCode = validateRejectBarcode(barcodeValue);
      if (!validRejectCode) {
        await showDialog("alter", "유효한 불량유형 바코드가 아닙니다.");
        focusRejectBarcodeInput();
        return;
      }
      const scanCode = normalizeBarcode(validRejectCode);
      let matched = false;

      gridRefs.current["grid1"].current?.forEachNode?.((node: any) => {
        if (matched) return;
        const rawReject = (node?.data?.cd_reject ?? "").toString();
        const normReject = normalizeBarcode(rawReject);
        if (normReject !== scanCode) return;
        node.setDataValue("qt_b", (Number(node.data?.qt_b ?? 0) || 0) + 1);

        matched = true;
      });

      if (!matched)
        gridRefs.current["grid1"].current?.addRow?.(
          createRejectRow(scanCode, 1)
        );

      // 상단 form2 불량수량/실적수량 합계 동기화
      await recalcRejectQtyToPanel();
      setRejectBarcode("");
      restoreRejectBarcodeFocus();
    };

    const createRejectRow = (cdReject = "", qtB = 0) => {
      const maxNoLine =
        gridRefs.current["grid1"].current?.getMaxValue?.("no_line") ?? 0;
      return {
        cd_firm: userInfo.cd_firm,
        no_run: "",
        no_line: Number(maxNoLine || 0) + 1,
        cd_reject: cdReject,
        qt_b: qtB,
        dc_rmk: "",
      };
    };

    // 현재 스캔 처리 대상 no_line_run을 결정한다.
    // 우선순위: grid1 포커스 행 -> pending 존재 여부 확인 -> 전체 pending 첫 행
    const resolveActiveNoLineRun = () => {
      const focusedNoLineRun =
        Number(getCurrentRunlLink()?.no_line_run ?? 0) || 0;
      let activeNoLineRun = focusedNoLineRun;

      if (activeNoLineRun > 0 && !hasPendingMaterialForRun(activeNoLineRun)) {
        activeNoLineRun = 0;
      }
      if (activeNoLineRun <= 0) {
        const firstPending = getAllGrid2Rows().find((row: any) =>
          isMatlRowPending(row)
        );
        activeNoLineRun = Number(firstPending?.no_line_run ?? 0) || 0;
      }

      return activeNoLineRun;
    };

    // 동일 no_line_run 범위에서 이미 스캔된 바코드인지 확인한다.
    const hasDuplicateBarcodeInRun = (
      activeNoLineRun: number,
      barcodeValue: string
    ) => {
      let dup = false;
      gridRefs.current["grid2"].current?.api?.forEachNode?.((node: any) => {
        if (dup) return;
        const row = node.data || {};
        if (
          Number(row?.no_line_run ?? 0) === activeNoLineRun &&
          normalizeBarcode(row.no_barcode) === normalizeBarcode(barcodeValue)
        ) {
          dup = true;
        }
      });
      return dup;
    };

    // partslist에서 스캔 바코드(SN/LOT)와 일치하고 가용잔량이 있는 항목을 찾는다.
    const findScannablePartByBarcode = (barcodeValue: string) => {
      return (partsListRef.current || []).find((p: any) => {
        const fg = (p?.fg_unit || "").toString().trim().toUpperCase();
        const sn = normalizeBarcode(p?.no_sn);
        const lot = normalizeBarcode(p?.no_lot);
        const v = normalizeBarcode(barcodeValue);
        if (fg === "SN" && sn === v) return getPartAvailableQty(p) > 0;
        if (fg === "LOT" && lot === v) return getPartAvailableQty(p) > 0;
        return false;
      });
    };

    // [투입자재 바코드 처리]
    // 투입자재 바코드 스캔 처리 (prd_issuel 조회 후 grid2 행 추가)
    const onMaterialBarcodeScan = async (
      barcodeValue: string,
      silent = false
    ) => {
      const debugFail = async (reason: string, extra?: Record<string, any>) => {
        if (silent) return;
        const rows = getAllGrid2Rows();
        const pendingCnt = rows.filter((r: any) => isMatlRowPending(r)).length;
        const lines = [
          `[MAT-SCAN DEBUG] ${reason}`,
          `scan:${(barcodeValue || "").toString()}`,
          `grid2.rows:${rows.length}`,
          `grid2.pending:${pendingCnt}`,
        ];
        if (extra) {
          Object.entries(extra).forEach(([k, v]) => {
            lines.push(`${k}:${v ?? ""}`);
          });
        }
        await showDialog("alter", lines.join("\n"));
      };

      const panel1 = formPanel1Ref.current?.getFormdata?.() || {};
      const noWo = panel1.no_wo || selectedData?.no_wo;
      if (!noWo) {
        await debugFail("작업지시 정보 없음", {
          panel_no_wo: panel1.no_wo || "",
          selected_no_wo: selectedData?.no_wo || "",
        });
        return false;
      }

      const activeNoLineRun = resolveActiveNoLineRun();

      // 투입자재 바코드 중복 스캔 방지
      // 정책: no_line_run이 다르면 동일 바코드 사용 가능
      if (hasDuplicateBarcodeInRun(activeNoLineRun, barcodeValue)) {
        await debugFail("이미 스캔된 바코드(동일 no_line_run)", {});
        return false;
      }

      const localHit = findScannablePartByBarcode(barcodeValue);

      const iss = localHit;
      if (!iss) {
        await debugFail("partslist/prd_issuel에서 바코드 미매칭", {
          no_wo: noWo,
        });
        return false;
      }

      const cdMatl = (iss.cd_matl || "").toString().trim();
      // 동일 자재가 여러 no_line_bill로 존재하면 iss.no_line_bill 기준으로 매핑
      // partslist(issuel) 1건을 자재기준행(wobill) 1건에 매핑한다.
      // no_line_bill이 있으면 동일 자재코드 내에서도 자재항번까지 일치시킨다.
      const baseRow = matlCheckRows.find(
        (r: any) => (r.cd_matl || "").toString().trim() === cdMatl
      );

      if (!baseRow) {
        await debugFail("matlCheckRows에서 cd_matl/no_line_bill 매핑 실패", {
          iss_cd_matl: iss.cd_matl || "",
          iss_no_line_bill: Number(iss.no_line_bill ?? 0) || 0,
        });
        return false;
      }

      const qtRemain = Number(iss.qt_remain ?? 0) || 0;
      if (qtRemain <= 0) {
        await debugFail("qt_remain <= 0", {
          iss_qt_remain: qtRemain,
          iss_no_io: iss.no_io || "",
          iss_no_line_io: Number(iss.no_line_io ?? 0) || 0,
          iss_no_line_lot_io: Number(iss.no_line_lot_io ?? 0) || 0,
        });
        return false;
      }
      // partslist.qt_use를 반영한 현재 가용잔량
      const issKey = makePartKey(iss);
      const partRow = (partsListRef.current || []).find(
        (p: any) => makePartKey(p) === issKey
      );
      const effectiveRemain =
        partRow != null
          ? getPartAvailableQty(partRow)
          : qtRemain - (Number(iss.qt_use ?? 0) || 0);
      if (effectiveRemain <= 0) {
        await debugFail("effectiveRemain <= 0", {
          effectiveRemain,
          iss_qt_remain: qtRemain,
          part_qt_use: partRow ? Number(partRow.qt_use ?? 0) || 0 : 0,
        });
        return false;
      }
      const grid2Rows = getAllGrid2Rows();
      const pendingByMatl = grid2Rows.filter((row: any) => {
        if ((row.cd_matl || "").toString().trim() !== cdMatl) return false;
        if (!isMatlRowPending(row)) return false;
        if (
          activeNoLineRun > 0 &&
          Number(row.no_line_run ?? 0) !== activeNoLineRun
        )
          return false;
        if (
          Number(iss?.no_line_bill ?? 0) > 0 &&
          Number(row.no_line_bill ?? 0) !== Number(iss?.no_line_bill ?? 0)
        )
          return false;
        return true;
      });
      const target = pendingByMatl[0];

      if (!target) {
        await debugFail("대상 pending row 매칭 실패", {
          need_cd_matl: (cdMatl || "").toString(),
          need_no_line_bill: Number(iss?.no_line_bill ?? 0) || 0,
          active_no_line_run: activeNoLineRun,
          sample_rows: grid2Rows
            .slice(0, 8)
            .map(
              (r: any) =>
                `matl:${(r.cd_matl || "").toString()},bill:${Number(
                  r.no_line_bill ?? 0
                )},run:${Number(r.no_line_run ?? 0)},fg:${(
                  r.fg_unit || ""
                ).toString()},barcode:${(r.no_barcode || "").toString()}`
            )
            .join(" | "),
        });
        return false;
      }

      // prepare 단계에서 행별 필요수량을 qt로 분해해두므로 qt 우선 사용
      const targetQty =
        Number(target.qt ?? target.qt_w ?? target.qt_unit ?? 0) || 0;
      const appliedQty =
        targetQty > 0 ? Math.min(targetQty, effectiveRemain) : effectiveRemain;

      const scannedFgUnit = (iss.fg_unit || target.fg_unit || "")
        .toString()
        .toUpperCase();
      const resolvedBarcode = (barcodeValue || "").toString().trim();
      // 표시용 no_barcode와 저장용 no_sn/no_lot를 타입 기준으로 강제 동기화
      const resolvedNoSn = scannedFgUnit === "SN" ? resolvedBarcode : "";
      const resolvedNoLot = scannedFgUnit === "LOT" ? resolvedBarcode : "";

      const updatedRow = {
        ...target,
        no_barcode: resolvedBarcode,
        no_sn: resolvedNoSn,
        no_lot: resolvedNoLot,
        fg_unit: scannedFgUnit || iss.fg_unit,
        no_io: iss.no_io,
        no_line_io: iss.no_line_io,
        no_line_lot_io: iss.no_line_lot_io,
        no_origin: iss.no_origin,
        no_line_origin: iss.no_line_origin,
        no_line_lot_origin: iss.no_line_lot_origin,
        no_ct: iss.no_ct,
        no_plt: iss.no_plt,
        cd_wh: iss.cd_wh,
        qt: appliedQty,
        qt_w: appliedQty,
        qt_b: 0,
      };
      let nextGrid2Rows = grid2Rows.map((row: any) =>
        row === target ? updatedRow : row
      );
      gridRefs.current["grid2"].current?.applyTransaction?.({
        update: [updatedRow],
      });
      // 수동 스캔행은 스캔 성공 시점에 qt_use를 누적한다.
      if (issKey && appliedQty > 0) {
        const used = new Map<string, number>();
        used.set(issKey, appliedQty);
        applyPartUseMap(used);
        // FE에서도 즉시 yn_check를 올려, 저장 전 다음 실적 입력 시 자동 바코드 주입이 가능하도록 한다.
        const nextParts = (partsListRef.current || []).map((p: any) => {
          if (makePartKey(p) !== issKey) return p;
          return {
            ...p,
            yn_check: "Y",
          };
        });
        partsListRef.current = nextParts;
      }

      // 선택한 바코드 잔량이 대상 필요수량보다 작으면 부족분 행을 추가하여 추가 스캔 가능하게 유지
      if (targetQty > appliedQty) {
        const remainQty = targetQty - appliedQty;
        const remainRow = {
          ...target,
          qt: remainQty,
          qt_w: remainQty,
          qt_b: 0,
          no_barcode: "",
          no_sn: "",
          no_lot: "",
          no_io: "",
          no_line_io: 0,
          no_line_lot_io: 0,
          no_origin: "",
          no_line_origin: 0,
          no_line_lot_origin: 0,
          no_ct: "",
          no_plt: "",
          cd_wh: "",
        };
        nextGrid2Rows = [...nextGrid2Rows, remainRow];
        gridRefs.current["grid2"].current?.applyTransaction?.({
          add: [remainRow],
        });
      }

      setDataSources((prev: any) => ({
        ...prev,
        grid2: nextGrid2Rows,
      }));

      focusProductBarcodeInput(50);
      return true;
    };

    // [조회/초기화]
    // 작업일 기준으로 근무일/작업조를 조회한다(fn_prd_dt_work).
    const loadWorkDateInfo = async (dtRun?: string) => {
      const targetDate = (dtRun || dateToString(new Date())).toString();
      const payload = {
        id: "fn_prd_dt_work",
        params: {
          cd_firm: userInfo.cd_firm,
          dt: targetDate,
        },
      };

      const data: any = await fnGetTableById(payload);
      if (Array.isArray(data) && data.length > 0) {
        return {
          dt_run: data[0].dt_work || targetDate,
          cd_shift: data[0].cd_flag || "",
        };
      }

      return {
        dt_run: targetDate,
        cd_shift: "",
      };
    };

    // dt_run 변경 시 작업일/작업조를 재조회해 panel3 값을 동기화한다.
    const onPanel3ValueChanged = async (event: any) => {
      const colId = (event?.colId || "").toString();

      // 양품수량/불량수량 수정 시 실적수량(qt)을 즉시 합계로 동기화
      if (colId === "qt_w" || colId === "qt_b") {
        const panel2 = formPanel2Ref.current?.getFormdata?.() || {};
        const qtW = toNum(panel2.qt_w);
        const qtB = toNum(panel2.qt_b);
        await formPanel2Ref.current?.setDataValue?.("qt", qtW + qtB, "noEvent");
        return {};
      }

      if (colId !== "dt_run") return {};
      const nextDate = (event.newValue || "").toString();
      if (!nextDate) return {};

      try {
        const workInfo = await loadWorkDateInfo(nextDate);
        if (workInfo.dt_run && workInfo.dt_run !== nextDate) {
          await formPanel2Ref.current?.setDataValue?.(
            "dt_run",
            workInfo.dt_run,
            "noEvent"
          );
        }
        await formPanel2Ref.current?.setDataValue?.(
          "cd_shift",
          workInfo.cd_shift || "",
          "noEvent"
        );
      } catch (error: any) {
        showDialog("error", error.message);
      }
      return {};
    };

    const onEnter = async (sender: string, value: string) => {
      switch (sender) {
        case "no_lot":
          handleProductionBarcodeSubmit(value);
          setTab(TAB_MATERIAL);
          break;
      }
    };

    // [입력/포커스 유틸]
    const focusInput = (
      inputRef: React.MutableRefObject<HTMLInputElement | null>,
      delay = 0
    ) => {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select?.();
      }, delay);
    };
    const focusRejectBarcodeInput = (delay = 0) =>
      focusInput(rejectBarcodeRef, delay);
    const focusProductBarcodeInput = (delay = 0) =>
      focusInput(prodBarcodeRef, delay);
    const toTrimmedString = (value: any) => (value ?? "").toString().trim();
    const moveToRejectTabAndRestoreProdFocus = () => {
      setTab(TAB_REJECT);
      setTimeout(() => focusBarcodeInputWithRetry(), 60);
    };

    // header 값으로 panel2 반영값(qt_w/fg_unit/yn_auto)을 생성한다.
    const getPanel2PatchFromHeader = (header: any) => {
      const fgUnit = (header?.fg_unit || "").toString();
      // 26.06.19 kdy mas_item.qt_lotsize 값 있으면 들어가게
      const qtW =
        Number(
          header?.qt_lotsize !== 0
            ? header?.qt_lotsize
            : (header?.qt_remain ?? 0)
        ) || 0;
      return {
        qt_w: qtW,
        // 조회 시 불량수량은 항상 0부터 시작해야 grid1 합계와 일관된다.
        qt_b: 0,
        qt: qtW,
        fg_unit: fgUnit,
        // FE 고정 규칙: LOT면 자동채번(Y), SN/QT는 N
        yn_auto: (header?.yn_prd_barcode_auto || "N").toString(),
      };
    };

    // WP2001은 prd_runl을 1건(no_line=1)만 저장하므로,
    // 필수 스캔 대상 자재는 조회 직후 no_line_run=1의 pending row로 미리 생성한다.
    // 이후 바코드 스캔 성공 시 onMaterialBarcodeScan에서 재고 연결키와 LOT/SN 값을 채운다.
    const buildInitialMaterialRows = (rows: any[]) => {
      return (Array.isArray(rows) ? rows : []).map((row: any) => {
        const qtNeed = Number(row?.qt_unit ?? row?.qt ?? row?.qt_w ?? 0) || 0;
        return {
          ...row,
          no_run: "",
          no_line_run: 1,
          no_barcode_item: "",
          no_barcode: "",
          no_sn: "",
          no_lot: "",
          no_io: "",
          no_line_io: 0,
          no_line_lot_io: 0,
          no_origin: "",
          no_line_origin: 0,
          no_line_lot_origin: 0,
          no_ct: "",
          no_plt: "",
          cd_wh: "",
          qt: qtNeed,
          qt_w: qtNeed,
          qt_b: 0,
        };
      });
    };

    // 조회 직후 하단 그리드/탭/포커스를 초기 상태로 맞춘다.
    const resetSearchResultUi = (materialRows: any[] = []) => {
      setProdBarcode("");
      setDataSources({
        grid1: [],
        // Y 대상 소요자재는 저장될 단일 실적행(no_line_run=1)에 연결될 스캔 대기행이다.
        grid2: materialRows,
        grid3: [],
      });
      // 하단은 항상 첫 번째 탭(불량내역)부터 시작
      setTab(TAB_REJECT);
      focusRejectBarcodeInput(60);
    };

    const prepareRunNoForAttachment = async () => {
      const selectedNoRun = (selectedData?.no_run || "").toString().trim();
      if (selectedNoRun) {
        setSelectedDocu(selectedNoRun);
        setSelectedLineDocu(0);
        await formPanel2Ref.current?.setDataValue?.(
          "no_run",
          selectedNoRun,
          "noEvent"
        );
        return selectedNoRun;
      }

      const runhData = formPanel2Ref.current?.getFormdata?.() || {};
      const dtRun =
        runhData.dt_run || panel2Data?.dt_run || dateToString(new Date());
      const cd_module: any = Menuprops?.cd_module;
      const cd_menu: any = Menuprops?.cd_menu;
      if (!cd_module || !cd_menu || !dtRun) {
        setSelectedDocu("");
        setSelectedLineDocu(0);
        return "";
      }

      const noRun = await getAutoSlip(cd_module, 2, dtRun, cd_menu);
      const nextNoRun = (noRun || "").toString().trim();
      setSelectedDocu(nextNoRun);
      setSelectedLineDocu(0);
      setPanel2Data((prev: any) => ({ ...(prev || {}), no_run: nextNoRun }));
      await formPanel2Ref.current?.setDataValue?.(
        "no_run",
        nextNoRun,
        "noEvent"
      );
      return nextNoRun;
    };

    const onSearchRunView = async () => {
      const noRun = (selectedData?.no_run || "").toString().trim();
      if (!noRun) return;

      const params = {
        id: "sn_prd_wrk_reg_001_pop_run_01",
        params: {
          cd_firm: userInfo.cd_firm,
          no_run: noRun,
        },
      };

      const data: any = await fnGetDataSet(params);
      const woHeader = data?.[0]?.[0] || {};
      const runh = data?.[1]?.[0] || {};
      const rejectRows = Array.isArray(data?.[3]) ? data[3] : [];
      const inspRows = Array.isArray(data?.[4]) ? data[4] : [];
      const conslRows = Array.isArray(data?.[6]) ? data[6] : [];

      setPanel1Data(woHeader);
      setPanel2Data({ ...runh, no_run: noRun });
      setMatlCheckRows([]);
      partsListRef.current = [];
      setHasMatlCheck(conslRows.length > 0);
      setProdBarcode("");
      setRejectBarcode("");
      setDataSources({
        grid1: rejectRows,
        grid2: conslRows,
        grid3: inspRows,
      });
      setTab(TAB_REJECT);
      await prepareRunNoForAttachment();
    };

    const onSearch = async () => {
      try {
        if (isRunViewMode) {
          await onSearchRunView();
          return;
        }

        const params = {
          id: "sn_prd_wrk_reg_001_pop_01",
          params: {
            cd_firm: userInfo.cd_firm,
            no_wo: selectedData?.no_wo,
            no_line: selectedData?.no_line,
          },
        };

        const data: any = await fnGetDataSet(params);
        const header = data?.[0]?.[0] || {};
        const matlCheckList =
          !isRework && Array.isArray(data?.[1]) ? data[1] : [];
        const partsList = Array.isArray(data?.[2]) ? data[2] : [];
        // Y 대상만 grid2 기본행으로 노출한다.
        // N 대상은 저장 직전 자동소진 로직으로 prd_consl에 합산한다.
        const matlCheckFiltered = matlCheckList.filter((row: any) =>
          isYnY(row?.yn_matl_check)
        );
        const hasMatlCheckY = matlCheckFiltered.length > 0;
        const materialRows = buildInitialMaterialRows(matlCheckFiltered);

        setPanel1Data(header);
        setPanel2Data((prev: any) => ({
          ...(prev || {}),
          ...getPanel2PatchFromHeader(header),
        }));
        setMatlCheckRows(matlCheckList);
        partsListRef.current = partsList;

        setHasMatlCheck(hasMatlCheckY);
        resetSearchResultUi(materialRows);
        await prepareRunNoForAttachment();
      } catch (error: any) {
        showDialog("error", error.message);
      }
    };

    // [초기화] 팝업 오픈 시 기본 컨트롤/기본값을 세팅하고 첫 조회를 실행한다.
    const initializePopupOnOpen = async () => {
      try {
        const rtn: any = await getInitPopUpData("SN_PRD_POP_WP2001", {});
        const form1 = rtn.form.form1 || [];
        const form2 = rtn.form.form2 || [];

        const workInfo = await loadWorkDateInfo(dateToString(new Date()));
        const panel2Defaults = {
          dt_run: workInfo.dt_run,
          cd_emp: userInfo.cd_emp,
          nm_emp: userInfo.nm_emp,
          fg_run: selectedData?.fg_run || "S",
          cd_shift: workInfo.cd_shift || "A",
        };

        setPanel1Controls(toReadOnlyControls(form1));
        setPanel2Controls(toReadOnlyControls(form2));
        setPanel1Data(rtn.form.form1ControlData || {});
        setPanel2Data({
          ...(rtn.form.form2ControlData || {}),
          ...panel2Defaults,
        });

        const grid1Columns = rtn.grid.grid1 || [];
        rejectCodeListRef.current =
          extractRejectCodeListFromGridColumns(grid1Columns);
        setColums({
          grid1: grid1Columns,
          grid2: rtn.grid.grid2 || [],
          grid3: rtn.grid.grid3 || [],
        });

        // form/grid 기본값 반영 후 초기 조회
        await new Promise((resolve) => setTimeout(resolve, 0));
        await onSearch();
        setFocus("no_lot");
      } catch (error: any) {
        showDialog("error", error.message);
      }
    };

    useImperativeHandle(ref, () => ({
      getSelectedValue: async () => {
        if (isRunViewMode) {
          return { excelUpload: false };
        }
        const result = await onSave();
        return { excelUpload: result === true };
      },
    }));

    // 탭 전환 시 탭별 바코드 입력칸으로 포커스를 이동한다.
    useEffect(() => {
      setTimeout(() => {
        if (tab === TAB_REJECT) {
          focusRejectBarcodeInput();
          return;
        }
        if (tab === TAB_MATERIAL) {
          focusProductBarcodeInput();
        }
      }, 60);
    }, [tab]);

    useEffect(() => {
      if (!shouldRestoreProdFocusRef.current) return;
      if (toTrimmedString(prodBarcode)) return;
      shouldRestoreProdFocusRef.current = false;
      // setTimeout(() => focusBarcodeInputWithRetry(), 80);
    }, [dataSources.grid1, dataSources.grid2, prodBarcode]);
    // [초기화 트리거] 팝업 최초 오픈 시 초기화 함수 실행
    useEffect(() => {
      initializePopupOnOpen();
    }, []);

    // 그리드 콤보/팝업 레이어를 현재 모달 루트 기준으로 렌더링
    useEffect(() => {
      setPopupParentEl(popupRootRef.current);
    }, []);

    useEffect(() => {
      if (!focus) return;

      formPanel2Ref.current?.setFocus(focus);
      setFocus(null);
    }, [panel2Data, panel2Controls]);

    // [저장 검증]
    // 저장 전 필수 스캔자재(YN_MATL_CHECK='Y') 누락 여부를 검증한다.
    const validateMaterialRowsBeforeSave = async (grid2Rows: any[]) => {
      const validationResult = validateMaterialRowsBeforeSaveCommon(
        hasMatlCheck,
        grid2Rows
      );
      if (validationResult.ok) return true;

      if (validationResult.reason === "no_rows") {
        await showDialog("alter", "투입자재 스캔 내역이 없습니다.");
        moveToRejectTabAndRestoreProdFocus();
        return false;
      }

      const missingBarcode = validationResult.row;
      const matlCode = (missingBarcode?.cd_matl || "").toString().trim();
      const matlName = (missingBarcode?.nm_matl || "").toString().trim();
      await showDialog(
        "alter",
        `투입자재[${matlCode} : ${matlName}]의 바코드가 누락된 행이 존재합니다.`
      );
      setTab(TAB_REJECT);
      setTimeout(() => {
        const grid2Api = gridRefs.current["grid2"].current?.api;
        const rowIndex = grid2Rows.findIndex(
          (row: any) => row === missingBarcode
        );
        if (rowIndex > -1 && grid2Api) {
          grid2Api.ensureIndexVisible?.(rowIndex, "middle");
          grid2Api.setFocusedCell?.(rowIndex, "no_barcode");
          grid2Api.getDisplayedRowAtIndex?.(rowIndex)?.setSelected?.(true);
        }
      }, 60);
      return false;
    };

    // 자동소진 대상(N)은 별도 팝업에서 확정 입력받아 prd_consl로 합산한다.
    const openManualConsPopup = async (qt: number) => {
      const component = (
        <PRD_MANUAL_CONS_POP_01
          selectedData={{
            no_wo: selectedData?.no_wo,
            no_line: selectedData?.no_line,
            qt,
          }}
          Menuprops={Menuprops}
        />
      );

      const result = await showResizablePopupModal({
        title: "투입자재 도움창",
        children: component,
        validation: true,
      });

      // 취소/닫기 계열은 저장 중단으로 처리
      if (
        result === undefined ||
        result === null ||
        result === "close" ||
        result === "cancel" ||
        result === false
      ) {
        return null;
      }
      return Array.isArray(result) ? result : [];
    };

    // [payload 생성]
    const buildPopupConslInsert = (
      popupRows: any[],
      noRun: string,
      startNoLine: number
    ) => {
      const rows = Array.isArray(popupRows) ? popupRows : [];
      return rows
        .filter((row: any) => (row?.cd_matl || "").toString().trim() !== "")
        .map((row: any, idx: number) => {
          const fgUnit = (row?.fg_unit || "").toString().toUpperCase();
          const noLot = (row?.no_lot || "").toString();
          const noSn = (row?.no_sn || "").toString();
          const noBarcode =
            (row?.no_barcode || "").toString() ||
            (fgUnit === "LOT" ? noLot : fgUnit === "SN" ? noSn : "");
          const qtVal = Number(row?.qt ?? row?.qt_w ?? 0) || 0;
          const qtWVal = Number(row?.qt_w ?? qtVal) || 0;
          const qtBVal = Number(row?.qt_b ?? 0) || 0;

          return {
            cd_firm: userInfo.cd_firm,
            no_run: noRun,
            no_line: startNoLine + idx,
            cd_matl: row?.cd_matl,
            no_line_bill: row?.no_line_bill,
            no_line_run: 1,
            fg_unit: row?.fg_unit,
            no_lot: noLot,
            no_sn: noSn,
            qt: qtVal,
            qt_w: qtWVal,
            qt_b: qtBVal,
            no_io: row?.no_io,
            no_line_io: row?.no_line_io,
            no_line_lot_io: row?.no_line_lot_io,
            no_origin: row?.no_origin,
            no_line_origin: row?.no_line_origin,
            no_line_lot_origin: row?.no_line_lot_origin,
            no_ct: row?.no_ct,
            no_plt: row?.no_plt,
            cd_wh: row?.cd_wh,
            no_barcode: noBarcode,
          };
        });
    };

    // 스캔필수(Y) 자재의 소요량(qt_unit) 충족 여부를 최종 검증
    const validateYMaterialNeed = async (conslRows: any[]) => {
      if (!hasMatlCheck) return true;
      // 공통 저장헬퍼: Y 자재 소요량 부족 계산
      const shortageInfo = findYMaterialShortage(matlCheckRows, conslRows);
      if (shortageInfo) {
        await showDialog(
          "alter",
          `[${shortageInfo.matlCode}] 소요수량이 부족합니다. 부족수량: ${shortageInfo.shortage}`
        );
        moveToRejectTabAndRestoreProdFocus();
        return false;
      }
      return true;
    };

    // // prd_consl -> prd_issuel 연결키 정합성 검증
    // // (cd_firm,no_io,no_line_io,no_line_lot_io) 누락 시 저장 전 차단
    // const validateConslIssueKeys = async (conslRows: any[]) => {
    //   // 공통 저장헬퍼: 연결키 누락 행 탐지
    //   const invalid = findInvalidConslIssueKeyRow(conslRows);
    //   if (!invalid) return true;

    //   const matlLabel = `${invalid.cd_matl || ""}`;
    //   await showDialog(
    //     "alter",
    //     `[${matlLabel}] 재고 연결키(no_io/no_line_io/no_line_lot_io)가 누락되었습니다.`
    //   );
    //   moveToRejectTabAndRestoreProdFocus();
    //   return false;
    // };

    // 투입번호(no_cons) 채번 후 prd_consh 1건을 생성한다.
    const issueNoConsAndBuildConsh = async (runhData: any, panel1Data: any) => {
      if (!runhData.dt_run) {
        await showDialog("alter", "실적일이 없습니다.");
        return null;
      }
      const cd_module: any = Menuprops?.cd_module;
      if (!cd_module) {
        await showDialog(
          "error",
          "전표 채번 모듈 정보가 없습니다. 관리자에게 문의하세요."
        );
        return null;
      }
      const no_cons = await getAutoSlip(
        cd_module,
        61,
        runhData.dt_run,
        "SN_PRD_WRK_REG_001"
      );
      if (!no_cons) {
        await showDialog("error", "투입번호 채번에 실패했습니다.");
        return null;
      }

      const ynBf = (matlCheckRows[0]?.yn_bf || "N").toString();
      // 공통 저장헬퍼: prd_consh.insert 생성
      const conshInsert = buildConshInsertRows(
        userInfo.cd_firm,
        no_cons,
        runhData.no_run,
        selectedData?.no_wo,
        selectedData?.no_line,
        panel1Data.cd_op,
        ynBf,
        runhData.dt_run
      );
      return { no_cons, conshInsert };
    };

    // [저장 실행]
    // 저장: no_run 채번 -> runh/runl payload 구성 -> fnSave.
    const onSave = async () => {
      try {
        gridRefs.current["grid1"].current?.stopEditing();

        const runhData = formPanel2Ref.current?.getFormdata?.() || {};
        const panel1Data = formPanel1Ref.current?.getFormdata?.() || {};
        const grid2Rows: any[] = getAllGrid2Rows();
        const grid1Rows: any[] = getAllGrid1Rows();
        const panelQtW = toNum(runhData?.qt_w);
        const headerQtRemain = toNum(panel1Data?.qt_remain);

        // no_lot입력 가능하게 활성화면서
        // 자동채번이 아니면
        if (
          !formPanel2Ref.current?.getIsReadOnly("no_lot") &&
          runhData.yn_auto === "N" &&
          (runhData.no_lot === "" ||
            runhData.no_lot === null ||
            runhData.no_lot === undefined)
        ) {
          await showDialog("alter", "실적 LOT번호를 입력해주세요.");
          return false;
        }

        if (panelQtW > headerQtRemain) {
          await showDialog("alter", "이전 실적 수량을 초과했습니다.");
          return false;
        }

        // 소요량 재계산에 쓰이므로 문자열 결합을 피하기 위해 숫자로 변환해 둔다.
        const qtW = toNum(runhData?.qt_w);
        const qtB = toNum(runhData?.qt_b);

        // 불량실적수량은 불량내역에서 확정할 수 없으므로 유효범위로 검증한다.
        const rejectQtyError = findRejectQtyError(runhData, grid1Rows);
        if (rejectQtyError) {
          await showDialog("alter", rejectQtyError);
          setTab(TAB_REJECT);
          focusRejectBarcodeInput(60);
          return false;
        }

        // 투입자재 지정이 필요한 경우(yn_matl_check=Y 존재) 저장 전 검증
        const validMaterialRows =
          await validateMaterialRowsBeforeSave(grid2Rows);
        if (!validMaterialRows) {
          return false;
        }

        let consInsertRows: any[] = [];
        const nMatls = matlCheckRows.filter(
          // 자동소진 팝업 대상: yn_matl_check='N' && yn_bf='N'
          (row: any) => !isYnY(row?.yn_matl_check) && !isYnY(row?.yn_bf)
        );
        const hasManualConsTarget = nMatls.length > 0;

        if (hasMatlCheck) {
          // prd_consl 생성: grid2 row 기반 (no_cons는 후반에 일괄 주입)
          // no_io/no_line_io/no_line_lot_io는 BE의 prd_issuel yn_check 갱신 키로도 사용된다.
          consInsertRows = buildManualConslInsertRows(
            userInfo.cd_firm,
            grid2Rows,
            ""
          );

          // 소요량 재계산
          consInsertRows.forEach((item) => {
            const qtUnit = item.qt;
            item.qt = qtUnit * (qtW + qtB);
            item.qt_w = qtUnit * qtW;
          });
        }

        // 자동소진 대상(N)은 PRD_MANUAL_CONS_POP_01 팝업 결과로만 처리
        if (hasManualConsTarget) {
          // 별도 쿼리 없이 matlCheckRows 조건 충족 시 팝업 오픈
          const qtRun = Number(runhData?.qt ?? 0) || 0;
          const popupRows = await openManualConsPopup(qtRun);
          if (popupRows === null) {
            return false;
          }
          if (!Array.isArray(popupRows) || popupRows.length < 1) {
            await showDialog("alter", "자동소진 자재의 처리내역이 없습니다.");
            return false;
          }
          const popupConsl = buildPopupConslInsert(
            popupRows,
            "",
            (consInsertRows?.length || 0) + 1
          );
          if (popupConsl.length < 1) {
            await showDialog("alter", "자동소진 자재의 처리내역이 없습니다.");
            return false;
          }
          consInsertRows = [...consInsertRows, ...popupConsl];
        }

        if (hasMatlCheck) {
          // Y 자재(스캔필수)와 N 자재(자동소진)를 합쳐서 최종 prd_consl payload를 만든다.
          const validNeed = await validateYMaterialNeed(consInsertRows || []);
          if (!validNeed) {
            return false;
          }
        }
        // if (consInsertRows.length > 0) {
        //   const validIssueKeys = await validateConslIssueKeys(
        //     consInsertRows || []
        //   );
        //   if (!validIssueKeys) return false;
        // }

        let no_run = runhData.no_run;
        if (!no_run) {
          const cd_module: any = Menuprops?.cd_module;
          const cd_menu: any = Menuprops?.cd_menu;
          if (!cd_module || !cd_menu) {
            await showDialog(
              "error",
              "실적번호 채번 정보가 없습니다. 관리자에게 문의하세요."
            );
            return false;
          }
          if (!runhData.dt_run) {
            await showDialog("alter", "실적일이 없습니다.");
            return false;
          }
          no_run = await getAutoSlip(cd_module, 2, runhData.dt_run, cd_menu);
          if (!no_run) {
            await showDialog("error", "실적번호 채번에 실패했습니다.");
            return false;
          }
        }

        runhData.no_run = no_run;
        // 제품실적과 연결된 투입자재 행에도 no_run 보정
        gridRefs.current["grid2"].current?.api?.forEachNode?.((node: any) => {
          if (!node?.data?.no_run) {
            node.setDataValue("no_run", no_run, "noEvent");
          }
        });

        const runlInsert = [
          {
            cd_firm: userInfo.cd_firm,
            no_run: no_run,
            no_line: 1,
            fg_unit: panel1Data?.fg_unit || "",
            qt: toNum(runhData?.qt),
            qt_w: toNum(runhData?.qt_w),
            qt_b: toNum(runhData?.qt_b),
            no_lot: runhData.no_lot,
          },
        ];

        const runlReInsert = grid1Rows
          .filter((row: any) => {
            const cdReject = (row?.cd_reject || "").toString().trim();
            return !!cdReject && toNum(row?.qt_b) > 0;
          })
          .map((row: any, idx: number) => ({
            cd_firm: userInfo.cd_firm,
            no_run: no_run,
            no_line: idx + 1,
            cd_reject: row?.cd_reject,
            qt_b: toNum(row?.qt_b),
            dc_rmk: (row?.dc_rmk || "").toString(),
          }));

        // prd_consl payload는 자재항번(no_line_bill) 기준으로 정렬한 뒤
        // no_line을 다시 부여해 저장 순서를 고정한다.
        const sortedConslRows = [...(consInsertRows || [])]
          .sort(
            (a: any, b: any) =>
              toNum(a?.no_line_bill) - toNum(b?.no_line_bill) ||
              toNum(a?.no_line) - toNum(b?.no_line)
          )
          .map((row: any, idx: number) => ({
            ...row,
            no_line: idx + 1,
          }));

        const savePayload: any = {
          prd_runh: {
            insert: [
              {
                ...runhData,
                cd_firm: userInfo.cd_firm,
                no_wo: selectedData?.no_wo,
                no_line_wo: selectedData?.no_line,
                cd_op: panel1Data.cd_op,
                st_run: "R",
                cd_item: panel1Data.cd_item,
                fg_unit: panel2Data?.fg_unit || "",
                yn_auto: runhData?.yn_auto || "N",
                yn_end: panel1Data.yn_end,
                cd_wh: panel1Data.cd_wh,
                cd_gp: userInfo.cd_gp,
                cd_dept: userInfo.cd_dept,
              },
            ],
            update: [],
            delete: [],
          },
          prd_runl: { insert: runlInsert, update: [], delete: [] },
          prd_runl_re: { insert: runlReInsert, update: [], delete: [] },
          prd_consh: { insert: [], update: [], delete: [] },
          prd_consl: {
            insert: sortedConslRows.map((row: any) => ({
              ...row,
              no_run: no_run,
            })),
            update: [],
            delete: [],
          },
        };

        if (consInsertRows.length > 0) {
          const noConsInfo = await issueNoConsAndBuildConsh(
            runhData,
            panel1Data
          );
          if (!noConsInfo) {
            return false;
          }

          // prd_consl에 no_cons 주입
          savePayload.prd_consl.insert = (
            savePayload.prd_consl.insert || []
          ).map((row: any) => ({
            ...row,
            no_cons: noConsInfo.no_cons,
          }));

          // prd_consh 생성 (실적 기준 1건)
          savePayload.prd_consh.insert = noConsInfo.conshInsert;
        }

        const saveResult = await fnSave(
          "SN_PRD_WRK_REG_001_SAVE_01",
          savePayload
        );
        if (saveResult === false) {
          return false;
        }
        const saveResultItem =
          saveResult && typeof saveResult === "object" ? saveResult : {};
        const issuedNoLot = (
          saveResultItem?.no_lot ||
          saveResultItem?.no_bsn ||
          ""
        )
          .toString()
          .trim();
        if (issuedNoLot) {
          await formPanel2Ref.current?.setDataValue?.(
            "no_lot",
            issuedNoLot,
            "noEvent"
          );
        }
        gridRefs.current["grid1"].current?.resetUpdate();
        return true;
      } catch (error: any) {
        showDialog("error", error.message);
        return false;
      }
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onCellValueChanged: async (event: any) => {
              const colId = (event?.colId || event?.column?.getColId?.() || "")
                .toString()
                .trim();
              if (colId === "qt_b") {
                await recalcRejectQtyToPanel();
              }
            },
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {},
        },
        grid3: {
          columns: columns.grid3,
          dataSource: dataSources.grid3,
          events: {},
        },
      }),
      [columns, dataSources]
    );

    return (
      <div
        className="h-full flex flex-col space-y-2 overflow-hidden"
        ref={popupRootRef}
      >
        <div className="p-2 min-h-[150px]">
          <SnTab
            titles={["지시정보"]}
            width={"100%"}
            selectedTab={"1"}
            onTabChanged={() => {}}
            header={
              <div className="flex justify-end gap-2">
                <SnButton
                  visible={false}
                  text="작업표준서"
                  onClick={() => {
                    showDialog("alter", "작업표준서 기능은 준비중입니다.");
                  }}
                />
                <SnButton
                  visible={false}
                  text="작업사양서"
                  onClick={() => {
                    showDialog("alter", "작업사양서 기능은 준비중입니다.");
                  }}
                />
              </div>
            }
            body={[
              <SnForm
                ref={formPanel1Ref}
                data={panel1Data}
                controls={panel1Controls}
              />,
            ]}
          />
        </div>
        <div className="p-2 min-h-[190px]">
          <SnTab
            titles={["실적정보"]}
            width={"100%"}
            selectedTab={"1"}
            onTabChanged={() => {}}
            body={[
              <SnForm
                ref={formPanel2Ref}
                data={panel2Data}
                controls={panel2Controls}
                etcProps={{ gridAlign: "start", allowXScroll: false }}
                events={{
                  onValueChanged: onPanel3ValueChanged,
                  onEnter: onEnter,
                }}
              />,
            ]}
          />
        </div>
        <div className="flex-1 min-h-0 flex w-full">
          <div className="min-h-0 h-full w-full">
            <SnTab
              titles={["불량내역", "투입자재", "검사항목"]}
              width={"100%"}
              selectedTab={tab}
              onTabChanged={(index: any) => {
                setTab(index);
                setTimeout(() => {
                  if (index === TAB_REJECT) {
                    focusRejectBarcodeInput();
                    return;
                  }
                  if (index === TAB_MATERIAL) {
                    focusProductBarcodeInput();
                    return;
                  }
                  if (index === TAB_INSPECTION) {
                    const grid3Api = gridRefs.current["grid3"].current?.api;
                    const firstCol = grid3Api
                      ?.getAllDisplayedColumns?.()?.[0]
                      ?.getColId?.();
                    if (firstCol != null) {
                      grid3Api?.setFocusedCell?.(0, firstCol);
                    }
                  }
                }, 0);
              }}
              header={[
                <div className="flex items-center justify-start gap-3 p-1 w-full">
                  <span className="text-sm text-gray-700">바코드</span>
                  <input
                    ref={rejectBarcodeRef}
                    className="border rounded px-2 h-8 w-56"
                    placeholder="불량유형 바코드를 스캔하세요"
                    value={rejectBarcode}
                    disabled={isRunViewMode}
                    onChange={(e) => setRejectBarcode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "NumpadEnter") {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRejectBarcodeSubmit(
                          (e.currentTarget as HTMLInputElement).value
                        );
                      }
                    }}
                  />
                  <SnTabHeader
                    onBtnAddClick={isRunViewMode ? undefined : () => {
                      gridRefs.current["grid1"].current?.addRow?.(
                        createRejectRow("", 0)
                      );
                      setTimeout(() => {
                        recalcRejectQtyToPanel();
                      }, 0);
                    }}
                    onBtnDeleteClick={isRunViewMode ? undefined : () => {
                      gridRefs.current["grid1"].current?.deleteSelectedRows();
                      setTimeout(() => {
                        recalcRejectQtyToPanel();
                      }, 0);
                    }}
                  />
                </div>,
                <div className="flex items-center justify-start gap-3 p-1 w-full">
                  <span className="text-sm text-gray-700">바코드</span>
                  <input
                    ref={prodBarcodeRef}
                    className="border rounded px-2 h-8 w-56"
                    placeholder="투입자재 바코드를 스캔하세요"
                    value={prodBarcode}
                    disabled={isRunViewMode}
                    onChange={(e) => setProdBarcode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleProductionBarcodeSubmit(null);
                      }
                    }}
                  />
                </div>,
                <div className="w-full" />,
              ]}
              body={[
                <div className="min-h-0 h-full w-full flex flex-col">
                  <SnGrid
                    ref={gridRefs.current["grid1"]}
                    popupParent={popupParentEl || undefined}
                    columnConfig={grids.grid1.columns}
                    rowData={grids.grid1.dataSource}
                    etcProps={{
                      rowSelection: "single",
                      isEditable: !isRunViewMode,
                    }}
                    events={grids.grid1.events}
                  />
                </div>,
                <div className="min-h-0 h-full w-full flex flex-col">
                  <SnGrid
                    ref={gridRefs.current["grid2"]}
                    popupParent={popupParentEl || undefined}
                    columnConfig={grids.grid2.columns}
                    rowData={grids.grid2.dataSource}
                    etcProps={{
                      rowSelection: "single",
                    }}
                    events={grids.grid2.events}
                  />
                </div>,
                <div className="min-h-0 h-full w-full flex flex-col">
                  <SnGrid
                    ref={gridRefs.current["grid3"]}
                    popupParent={popupParentEl || undefined}
                    columnConfig={grids.grid3.columns}
                    rowData={grids.grid3.dataSource}
                    etcProps={{
                      rowSelection: "single",
                    }}
                    events={grids.grid3.events}
                  />
                </div>,
              ]}
            />
          </div>
        </div>
        <div className="px-2 pb-2">
          <SnFileUploadBox
            selectedDocu={selectedDocu}
            selectedLineDocu={selectedLineDocu}
          />
        </div>
      </div>
    );
  }
);

export default WP2001;
